import { ContractListDto, contractService, EContractStatus } from '../services/contracts/ContractService';
import { normalizeContractStatus } from './contractStatusUtils';

/** Janela em que a restauração automática de contratos cancelados é permitida. */
export const RESTORE_CANCELLED_WINDOW_DAYS = 90;

export function getRestoreWindowStartMs(now = Date.now()): number {
  return now - RESTORE_CANCELLED_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}

export function isCancelledWithinRestoreWindow(
  cancelledAt: string | Date,
  now = Date.now()
): boolean {
  return new Date(cancelledAt).getTime() >= getRestoreWindowStartMs(now);
}

export function getRestoreWindowExpiredReason(): string {
  return `Fora da janela de ${RESTORE_CANCELLED_WINDOW_DAYS} dias.`;
}

export type RestoreWindowCheckItem = {
  contractId: string;
  clientRazaoSocial: string;
  clientCnpj: string;
  cancelledAt: string | null;
  withinWindow: boolean;
};

export const RESTORE_PARALLELISM_LIMIT = 4;

export async function mapWithConcurrencyLimit<TItem, TResult>(
  items: TItem[],
  concurrencyLimit: number,
  mapper: (item: TItem, index: number) => Promise<TResult>
): Promise<TResult[]> {
  if (items.length === 0) return [];
  const limit = Math.max(1, Math.min(concurrencyLimit, items.length));
  const results = new Array<TResult>(items.length);
  let nextIndex = 0;

  const worker = async () => {
    while (true) {
      const current = nextIndex;
      nextIndex += 1;
      if (current >= items.length) return;
      results[current] = await mapper(items[current], current);
    }
  };

  await Promise.all(Array.from({ length: limit }, () => worker()));
  return results;
}

export async function checkRestoreWindowForContracts(
  contracts: ContractListDto[]
): Promise<{ expired: RestoreWindowCheckItem[]; eligible: ContractListDto[] }> {
  const histories = await mapWithConcurrencyLimit(
    contracts,
    RESTORE_PARALLELISM_LIMIT,
    async (contract) => {
      const history = await contractService.getContractStatusHistory(contract.id);
      return { contract, history };
    }
  );

  const expired: RestoreWindowCheckItem[] = [];
  const eligible: ContractListDto[] = [];

  for (const { contract, history } of histories) {
    const latestCancelled = history
      .filter((h) => normalizeContractStatus(h.status) === EContractStatus.Cancelado)
      .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime())[0];

    const withinWindow =
      normalizeContractStatus(contract.status) === EContractStatus.Cancelado &&
      Boolean(latestCancelled?.changedAt) &&
      isCancelledWithinRestoreWindow(latestCancelled!.changedAt);

    const item: RestoreWindowCheckItem = {
      contractId: contract.id,
      clientRazaoSocial: contract.clientRazaoSocial,
      clientCnpj: contract.clientCnpj,
      cancelledAt: latestCancelled?.changedAt ?? null,
      withinWindow,
    };

    if (withinWindow) {
      eligible.push(contract);
    } else {
      expired.push(item);
    }
  }

  return { expired, eligible };
}
